import React, { useState, useMemo } from "react";
import { TECH_STACK, TECH_CATEGORIES } from "../mock";
import * as LucideIcons from "lucide-react";
import {
  Search,
  Cpu,
  Layers,
  ShieldCheck,
  Zap,
  ArrowUpRight,
  SlidersHorizontal,
  Sparkles,
  Server,
  Database,
  Cloud,
  Workflow,
  CheckCircle2
} from "lucide-react";

export default function TechStackSection({
  title = "Technologies & Ecosystem We Build With",
  subtitle = "Our engineering stack combines battle-tested enterprise architectures with next-generation AI and automation.",
  showSearch = true,
  defaultCategory = "all",
  className = ""
}) {
  const [activeCategory, setActiveCategory] = useState(defaultCategory);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredTech = useMemo(() => {
    return TECH_STACK.filter((item) => {
      const matchesCategory =
        activeCategory === "all" || item.category === activeCategory;
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        item.name.toLowerCase().includes(q) ||
        item.desc.toLowerCase().includes(q) ||
        item.useCase.toLowerCase().includes(q) ||
        item.badge.toLowerCase().includes(q) ||
        item.tier.toLowerCase().includes(q);
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  return (
    <section className={`py-16 md:py-24 ${className}`}>
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="flex items-center gap-3 text-[11px] tracking-[0.25em] font-semibold">
          <span className="h-px w-8 bg-black" />
          OUR TECH STACK &amp; CAPABILITIES
        </div>
        <div className="mt-4 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <h2 className="font-orbitron font-black text-3xl md:text-5xl leading-[1.05] tracking-tight max-w-3xl">
              Engineered with <span className="italic font-normal text-black/60">modern, battle-tested tools</span>
            </h2>
            <p className="mt-4 text-sm md:text-base text-black/65 max-w-2xl leading-relaxed">
              {subtitle}
            </p>
          </div>

          <div className="hidden sm:flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black text-white text-[11px] font-bold tracking-wider">
              <Zap className="h-3.5 w-3.5 text-amber-400" /> 30+ PRODUCTION TOOLS
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-black/15 text-[11px] font-semibold text-black/70">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> SECURE &amp; SCALABLE
            </span>
          </div>
        </div>

        {/* Controls: Search & Category Tabs */}
        <div className="mt-10 space-y-4">
          {showSearch && (
            <div className="relative max-w-md">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-black/40 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tools (e.g., Python, React, Gemini, Supabase, Docker)..."
                className="w-full pl-10 pr-4 py-2.5 rounded-full border border-black/15 bg-white text-xs focus:outline-none focus:border-black transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[10px] text-black/40 hover:text-black font-semibold"
                >
                  CLEAR
                </button>
              )}
            </div>
          )}

          {/* Category Chips */}
          <div className="flex flex-wrap items-center gap-2 pt-2">
            {TECH_CATEGORIES.map((cat) => {
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold transition-all flex items-center gap-2 ${
                    isActive
                      ? "bg-black text-white shadow-sm"
                      : "border border-black/15 text-black/70 hover:bg-black/5 hover:text-black"
                  }`}
                >
                  <span>{cat.label}</span>
                  <span
                    className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                      isActive ? "bg-white/20 text-white" : "bg-black/5 text-black/50"
                    }`}
                  >
                    {cat.id === "all" ? TECH_STACK.length : TECH_STACK.filter(t => t.category === cat.id).length}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tech Grid */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTech.map((t) => {
            const IconComponent = LucideIcons[t.icon] || LucideIcons.Cpu;
            return (
              <div
                key={t.name}
                className="group rounded-2xl border border-black/12 bg-white p-5 md:p-6 hover:border-black hover:-translate-y-1 hover:shadow-[0_15px_40px_rgba(0,0,0,0.06)] transition-all flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-start justify-between gap-3">
                    <div className="h-10 w-10 rounded-xl bg-black text-white flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                      <IconComponent className="h-5 w-5" />
                    </div>
                    <span className="text-[10px] font-bold tracking-wider px-2.5 py-1 rounded-full bg-black/5 text-black/70">
                      {t.badge}
                    </span>
                  </div>

                  <h3 className="mt-4 font-orbitron font-bold text-base text-black tracking-tight flex items-center gap-2">
                    {t.name}
                  </h3>
                  <p className="mt-2 text-xs text-black/60 leading-relaxed">
                    {t.desc}
                  </p>
                </div>

                <div className="mt-5 pt-3.5 border-t border-black/10">
                  <div className="flex items-start gap-1.5">
                    <span className="text-[10px] font-bold tracking-widest text-black/40 uppercase shrink-0 mt-0.5">
                      APPLICATION:
                    </span>
                    <p className="text-[11px] text-black/80 font-medium leading-normal">
                      {t.useCase}
                    </p>
                  </div>
                  <div className="mt-3 flex items-center justify-between text-[10px]">
                    <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                      <CheckCircle2 className="h-3 w-3" /> {t.tier}
                    </span>
                    <span className="text-black/40 uppercase font-semibold tracking-wider">
                      {t.category.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredTech.length === 0 && (
          <div className="text-center py-12 border border-dashed border-black/20 rounded-2xl bg-black/[0.01]">
            <p className="font-orbitron font-bold text-base text-black">No matching technologies found</p>
            <p className="mt-1 text-xs text-black/50">Try clearing your search query or selecting another category.</p>
            <button
              onClick={() => { setSearchQuery(""); setActiveCategory("all"); }}
              className="mt-4 px-4 py-2 rounded-full bg-black text-white text-xs font-semibold"
            >
              Reset Filters
            </button>
          </div>
        )}

        {/* Engineering Architecture Highlights */}
        <div className="mt-12 rounded-3xl border border-black/12 bg-black text-white p-6 md:p-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-amber-400" />
                <p className="font-orbitron font-bold text-sm">Ultra-Low Latency</p>
              </div>
              <p className="text-xs text-white/70 leading-relaxed">
                Async Python FastAPI + Edge CDN caching ensure sub-100ms response times globally.
              </p>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-400" />
                <p className="font-orbitron font-bold text-sm">Bank-Grade Security</p>
              </div>
              <p className="text-xs text-white/70 leading-relaxed">
                End-to-end TLS 1.3, AES-256 encryption at rest, and full DPDP Act 2023 compliance.
              </p>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Cpu className="h-4 w-4 text-cyan-400" />
                <p className="font-orbitron font-bold text-sm">AI-Native Pipelines</p>
              </div>
              <p className="text-xs text-white/70 leading-relaxed">
                Multi-model orchestration across OpenAI, Gemini, Claude, and pgvector embeddings.
              </p>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Workflow className="h-4 w-4 text-purple-400" />
                <p className="font-orbitron font-bold text-sm">Seamless Integrations</p>
              </div>
              <p className="text-xs text-white/70 leading-relaxed">
                Automated CRM flows, WhatsApp Business API, webhook triggers, and Brevo SMTP relays.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
